import React, { useState } from "react";
import { Select, Tag, Tooltip } from "antd";
import type { SelectProps } from "antd";
import { SvgIcons } from "Icon/SvgIcons";
import { SmallTitle } from "../Typography/Titles";
import { trimStr } from "utils";

export interface OptionType {
  value: string;
  label?: string;
  logo?: string; // Add logo as an optional property
}

type TagRender = SelectProps<string | string[]>["tagRender"];

const createTagRender = (
  options: OptionType[],
  handleDeselect?: (value: string) => void,
  isRightIcon?: boolean,
  isSmall?: boolean,
): TagRender => {
  return (props) => {
    const { label, closable, onClose, value } = props;
    const option = options.find((option) => option.value === value);

    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleClose = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (onClose) {
        onClose(); // Perform the default close action
        if (value && typeof value === "string") {
          handleDeselect?.(value); // Call handleDeselect with the tag value
        }
      }
    };

    return (
      // <Tooltip title={label}>
        <Tag
          color="#F9F5FF"
          bordered={true}
          onMouseDown={onPreventMouseDown}
          closable={closable}
          onClose={handleClose}
          closeIcon={SvgIcons.closeIcon}
          style={{
            marginInlineEnd: 4,
            border: "1px solid #7F56D9",
            color: "#344054",
            display: "flex",
            alignItems: "center",
            textTransform: "capitalize",
            margin: "2.5px",
            fontWeight: 500,
            padding: isSmall ? "1.5px 2.5px" : "2px 4px",
            fontSize: isSmall ? "10px" : "",
          }}
        >
          {isRightIcon && (
            <span className="mr-[5px] cursor-pointer">
              {option?.logo ? (
                <img
                  src={option.logo}
                  style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                isSmall ? SvgIcons.smallCheckIconWithBg :
                  SvgIcons.checkIconWithBg
              )}
            </span>
          )}
          {/* {typeof (label) == "string" && trimStr(label, 18)} */}
          {label}

        </Tag>
      // </Tooltip>
    );
  };
};

interface SearchFilterDropdownProps {
  title?: string;
  options: OptionType[] | any;
  placeholder?: string;
  maxSelect?: number | null | undefined;
  onChange?: (value: string[]) => void;
  handleDeselect?: (value: string) => void;
  onInputChange?: (value: string) => void;
  defaultValue?: any;
  mode?: "multiple" | "tags" | undefined;
  showTooltip?: boolean;
  loading?: boolean;
  isRightIcon?: boolean;
  tooltipText?: string;
  disabled?: boolean;
  isBorder?: boolean;
  isSmall?: boolean;
  onBlur?: (value: string) => void;
  style?: any;
  maxTagCount?: number;
}

const SearchFilterDropdown: React.FC<SearchFilterDropdownProps> = ({
  title = "titles",
  options,
  placeholder = "Enter and select...",
  maxSelect = null,
  defaultValue = [],
  onChange,
  handleDeselect,
  onInputChange,
  mode,
  showTooltip = false,
  loading = false,
  tooltipText = "Tooltip",
  isRightIcon = true,
  disabled = false,
  isBorder = true,
  isSmall = false,
  onBlur,
  style={},
  maxTagCount
}) => {
  const [inputValue, setInputValue] = useState("");
  const defaultValuesArray = Array.isArray(defaultValue)
    ? defaultValue
    : [defaultValue].filter(Boolean);
  const handleSelect = (value: string) => {
    const newSelectedValues = [...defaultValuesArray, value];
    onChange && onChange(newSelectedValues);
    setInputValue("");
  };

  // Filter options to exclude the ones already selected
  const filteredOptions =
    mode === undefined
      ? options
      : options?.filter((option: any) => !defaultValuesArray.includes(option?.value));

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      onBlur && onBlur(inputValue);
      setInputValue("");
    }
  };

  return (
    <div style={{ position: "relative" }} className="w-full">
      <Select
        showSearch
        mode={mode}
        tagRender={createTagRender(options, handleDeselect, isRightIcon, isSmall)}
        placeholder={placeholder}
        size={isSmall ? "small" : "large"}
        style={{
          width: "100%",
          border: `${isBorder ? disabled ? "1px" : "2px solid #9E77ED" : ""}`,
          borderRadius: "10px",
          maxWidth: "100%",
          fontSize: isSmall ? "12px" : "14px",
          ...style
        }}
        options={filteredOptions}
        value={defaultValue}
        onSelect={handleSelect}
        onDeselect={handleDeselect}
        onSearch={(value) => {
          setInputValue(value);
          onInputChange?.(value);
        }}
        maxTagCount={maxTagCount ? maxTagCount : undefined}
        suffixIcon={showTooltip ? null : <span>{SvgIcons.downArrow}</span>}
        loading={loading}
        maxCount={typeof maxSelect === "number" ? maxSelect : undefined}
        disabled={disabled}
        onBlur={() => {
          onBlur?.(inputValue);
        }}
        onKeyDown={handleKeyDown}
        inputValue={inputValue}
      />
      {/* {showTooltip && (
        <Tooltip title={tooltipText}>
          <span
            style={{
              position: "absolute",
              top: "13px",
              right: "10px",
              cursor: "pointer",
            }}
          >
            {SvgIcons.questionSvg}
          </span>
        </Tooltip>
      )} */}
      {maxSelect !== null && title !== "hidden" ? (
        <SmallTitle
          text={
            defaultValue.length >= maxSelect ? "You reached your limit" : `${maxSelect} ${title} max`
          }
          font="font-normal"
          className={
            defaultValue.length >= maxSelect ? "text-text-error-600 mt-2" : "text-text-tertiary mt-2"
          }
        />
      ) :
        ""}
    </div>
  );
};

export default SearchFilterDropdown;
